import React, { useState, useEffect } from 'react';
import { API, Auth } from 'aws-amplify';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Flex, Text, Heading, View, Button, SelectField } from '@aws-amplify/ui-react';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community';
import Loadingmini from '../components/loadingmini';
import { usePersonalInsurancePendingSummary } from '../hooks/usePersonalInsurancePendingSummary';
import { notifyPersonalInsuranceReviewChanged } from '../components/personalinsurancenavsync';
import { recalledFleets, withFleets } from '../withFleets';

function reviewBadgeStyle(state) {
  if (state === 'awaiting_fleet_signoff') {
    return { background: '#fff3cd', color: '#856404', padding: '2px 6px', borderRadius: '4px' };
  }
  if (state === 'awaiting_dates') {
    return { background: '#fde8e8', color: '#9b1c1c', padding: '2px 6px', borderRadius: '4px' };
  }
  return { background: '#f5f5f5', color: '#555', padding: '2px 6px', borderRadius: '4px' };
}

function fleetsFromSearch(search) {
  const fleetsParam = new URLSearchParams(search).get('fleets');
  if (!fleetsParam) return [];
  return fleetsParam.split(',').map((f) => f.trim()).filter(Boolean);
}

function fleetOnLink(rowFleet, pageFleet) {
  return rowFleet || pageFleet || new URLSearchParams(window.location.search).get('fleets') || recalledFleets();
}

function FleetNavLink({ path, fleet, children }) {
  const navigate = useNavigate();
  const hrefFor = () => {
    const code = fleet
      || new URLSearchParams(window.location.search).get('fleets')
      || recalledFleets();
    return withFleets(path, code);
  };
  return (
    <a
      href={hrefFor()}
      onMouseDown={(event) => {
        event.currentTarget.setAttribute('href', hrefFor());
      }}
      onClick={(event) => {
        const href = hrefFor();
        event.currentTarget.setAttribute('href', href);
        if (
          event.defaultPrevented
          || event.button !== 0
          || event.metaKey
          || event.ctrlKey
          || event.shiftKey
          || event.altKey
        ) {
          return;
        }
        event.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}

export default function Personalinsurancereview() {
  const location = useLocation();
  const selected_fleet = fleetsFromSearch(location.search);
  const fleetCode = selected_fleet[0] || null;
  const [is_loading, updateIsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [res, updateRes] = useState({ resp: [], total_pending: 0 });
  const [approveRow, updateApproveRow] = useState(null);
  const [denyRow, updateDenyRow] = useState(null);
  const [effectiveDate, updateEffectiveDate] = useState(new Date());
  const [expirationDate, updateExpirationDate] = useState(
    new Date(new Date().setFullYear(new Date().getFullYear() + 1))
  );
  const [coverageType, updateCoverageType] = useState('both');
  const [actionError, updateActionError] = useState('');

  const { refresh: refreshSummary } = usePersonalInsurancePendingSummary({
    enabled: Boolean(fleetCode),
    fleetCode,
    refreshKey: count,
  });

  useEffect(() => {
    let cancelled = false;
    async function fetchPageData() {
      if (!fleetCode) {
        updateRes({ resp: [], total_pending: 0 });
        updateIsLoading(false);
        updateActionError('');
        return;
      }
      updateIsLoading(true);
      updateActionError('');
      try {
        const session = await Auth.currentSession();
        const jwt = session.getAccessToken().getJwtToken();
        const response = await API.get('adminWebTools', '/ago', {
          queryStringParameters: {
            back_end: process.env.REACT_APP_BACKEND,
            jwt,
            api_endpoint: 'api3:staff:personal_insurance_reviews',
            request_type: 'GET',
            mode: 'list',
            fleet_code: fleetCode,
          },
        });
        if (!cancelled) {
          updateRes(response);
        }
      } catch (e) {
        if (!cancelled) {
          updateRes({ resp: [], total_pending: 0, status: 'Error loading queue' });
        }
      }
      if (!cancelled) {
        updateIsLoading(false);
      }
    }
    fetchPageData();
    return () => {
      cancelled = true;
    };
  }, [count, fleetCode]);

  async function postAction(row, method, extra = {}) {
    updateIsLoading(true);
    updateActionError('');
    try {
      const session = await Auth.currentSession();
      const jwt = session.getAccessToken().getJwtToken();
      const query = {
        back_end: process.env.REACT_APP_BACKEND,
        jwt,
        api_endpoint: 'api3:staff:personal_insurance_reviews',
        request_type: 'POST',
        method,
        document_name: row.insurance_image_key,
        ...extra,
      };
      await API.get('adminWebTools', '/ago', { queryStringParameters: query });
      updateApproveRow(null);
      updateDenyRow(null);
      setCount((c) => c + 1);
      refreshSummary();
      notifyPersonalInsuranceReviewChanged();
    } catch (e) {
      updateActionError('Action failed. Please try again or use the document review page.');
      updateIsLoading(false);
    }
  }

  function formatDateForApi(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const columns = [
    {
      headerName: 'Fleet',
      field: 'fleet_code',
      filter: true,
      width: 110,
    },
    {
      headerName: 'Driver',
      field: 'driver_full_name',
      filter: true,
      width: 180,
      cellRenderer: (params) => {
        const id = params.data?.driver_id;
        if (!id) return params.value;
        const fleet = fleetOnLink(params.data?.fleet_code, fleetCode);
        return <FleetNavLink path={`/user/${id}`} fleet={fleet}>{params.value || id}</FleetNavLink>;
      },
    },
    {
      headerName: 'Card name (scanned)',
      field: 'scanned_name',
      filter: true,
      width: 180,
      cellRenderer: (params) => params.value || '—',
    },
    {
      headerName: 'Phone',
      field: 'phone',
      filter: true,
      width: 130,
    },
    {
      headerName: 'Uploaded',
      field: 'uploaded_image_datetime',
      filter: true,
      width: 160,
    },
    {
      headerName: 'Review state',
      field: 'review_state',
      filter: true,
      width: 160,
      cellRenderer: (params) => {
        const val = params.value || 'awaiting_activation';
        return <span style={reviewBadgeStyle(val)}>{val.replace(/_/g, ' ')}</span>;
      },
    },
    {
      headerName: 'View',
      width: 90,
      cellRenderer: (params) => {
        const key = params.data?.insurance_image_key;
        if (!key) return null;
        const fleet = fleetOnLink(params.data?.fleet_code, fleetCode);
        return <FleetNavLink path={`/userpersonalinsurancedoc/${key}`} fleet={fleet}>Open</FleetNavLink>;
      },
    },
    {
      headerName: 'Actions',
      width: 220,
      cellRenderer: (params) => {
        const row = params.data;
        if (!row?.insurance_image_key) return null;
        return (
          <View>
            <Button size="small" onClick={() => updateApproveRow(row)}>
              Approve
            </Button>
            <Button size="small" variation="destructive" onClick={() => updateDenyRow(row)}>
              Deny
            </Button>
          </View>
        );
      },
    },
  ];

  return (
    <Flex direction="column" style={{ padding: '1rem', width: '100%' }}>
      <Heading level={4}>Personal Insurance Review</Heading>
      <Text>
        Approve or deny driver-uploaded personal insurance cards for your fleets.
        {fleetCode ? ` Filtered to fleet: ${fleetCode}.` : ''}
      </Text>
      {!fleetCode ? (
        <Text style={{ marginTop: '0.75rem' }}>
          <i>Select a fleet using the sidebar fleet selector.</i>
        </Text>
      ) : (
        <>
      <Flex direction="row" gap="0.5rem" alignItems="center" style={{ margin: '0.75rem 0' }}>
        <Button size="small" onClick={() => setCount((c) => c + 1)}>
          Refresh
        </Button>
      </Flex>
      {actionError ? <Text variation="error">{actionError}</Text> : null}
      {is_loading ? (
        <Loadingmini />
      ) : (
        <View className="ag-theme-quartz" style={{ height: 520, width: '100%' }}>
          <AgGridReact
            key={fleetCode || 'none'}
            theme={themeQuartz}
            rowData={res.resp || []}
            columnDefs={columns}
            defaultColDef={{ sortable: true, resizable: true }}
          />
        </View>
      )}
      {!is_loading && (!res.resp || res.resp.length === 0) ? (
        <Text style={{ marginTop: '1rem' }}>No pending personal insurance uploads for this fleet.</Text>
      ) : null}
        </>
      )}

      {approveRow ? (
        <View
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <View style={{ background: '#fff', padding: '1.5rem', borderRadius: 8, maxWidth: 420, width: '90%' }}>
            <Heading level={5}>Approve insurance</Heading>
            <Text>
              {approveRow.driver_full_name} ({approveRow.fleet_code})
            </Text>
            <Text style={{ marginTop: '0.75rem' }}>Effective date (required)</Text>
            <DatePicker selected={effectiveDate} onChange={(d) => d && updateEffectiveDate(d)} />
            <Text style={{ marginTop: '0.75rem' }}>Expiration date (required)</Text>
            <DatePicker selected={expirationDate} onChange={(d) => d && updateExpirationDate(d)} />
            <SelectField
              label="Coverage type"
              value={coverageType}
              onChange={(e) => updateCoverageType(e.target.value)}
              style={{ marginTop: '0.75rem' }}
            >
              <option value="both">Both</option>
              <option value="physical">Physical</option>
              <option value="liability">Liability</option>
            </SelectField>
            <Flex direction="row" gap="0.5rem" style={{ marginTop: '1rem' }}>
              <Button
                variation="primary"
                onClick={() =>
                  postAction(approveRow, 'approve', {
                    effective_date: formatDateForApi(effectiveDate),
                    expiration_date: formatDateForApi(expirationDate),
                    coverage_type: coverageType,
                  })
                }
              >
                Confirm approve
              </Button>
              <Button onClick={() => updateApproveRow(null)}>Cancel</Button>
            </Flex>
          </View>
        </View>
      ) : null}

      {denyRow ? (
        <View
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <View style={{ background: '#fff', padding: '1.5rem', borderRadius: 8, maxWidth: 420, width: '90%' }}>
            <Heading level={5}>Deny insurance card?</Heading>
            <Text>
              {denyRow.driver_full_name} ({denyRow.fleet_code}) — the driver will need to upload a new card or
              choose AGO insurance.
            </Text>
            <Flex direction="row" gap="0.5rem" style={{ marginTop: '1rem' }}>
              <Button variation="destructive" onClick={() => postAction(denyRow, 'deny')}>
                Confirm deny
              </Button>
              <Button onClick={() => updateDenyRow(null)}>Cancel</Button>
            </Flex>
          </View>
        </View>
      ) : null}
    </Flex>
  );
}
